import React from 'react';
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import DialogView from './DialogView';
import PositionValue from './PositionValue';
import AddressValue from './AddressValue';
import CopyableText from './CopyableText';
import { MapIconButton } from './MapElement';
import usePositionAttributes from '../attributes/usePositionAttributes';
import { useTranslation } from './LocalizationProvider';
import StatusRow from './StatusRow';
import { useAttributePreference, usePreference } from '../util/preferences';
import {
  formatTime, formatDistance, formatCoordinate, get30DayDistanceColor,
} from '../util/formatter';
import { useGlobalStyles } from '../util/classes';

const useStyles = makeStyles((theme) => ({
  sectionCell: {
    borderBottom: 'none',
    paddingBottom: theme.spacing(0.5),
  },
  sectionDivider: {
    marginBottom: theme.spacing(1),
  },
}));

// The dialog used to be one undifferentiated run of rows, which made the
// install point and the current position easy to confuse.
const SectionRow = ({ title, first }) => {
  const classes = useStyles();
  return (
    <TableRow>
      <TableCell colSpan={2} className={classes.sectionCell} style={{ paddingTop: first ? 0 : 16 }}>
        {!first && <Divider className={classes.sectionDivider} />}
        <Typography variant="subtitle2">{title}</Typography>
      </TableCell>
    </TableRow>
  );
};

const PropsDialog = ({ device, position, open, handleClose }) => {
  const classes = useGlobalStyles();
  const t = useTranslation();
  const positionAttributes = usePositionAttributes(t);
  const distanceUnit = useAttributePreference('distanceUnit');
  const coordinateFormat = usePreference('coordinateFormat');

  const coordinates = (latitude, longitude) => `${formatCoordinate('latitude', latitude, coordinateFormat)}, ${formatCoordinate('longitude', longitude, coordinateFormat)}`;

  const coordinatesContent = (latitude, longitude) => (
    <Box display="flex" alignItems="center" gap={0.5}>
      <CopyableText text={coordinates(latitude, longitude)} />
      <MapIconButton latitude={latitude} longitude={longitude} type="google" color="secondary" size="small" />
      <MapIconButton latitude={latitude} longitude={longitude} type="bing" color="secondary" size="small" />
      <MapIconButton latitude={latitude} longitude={longitude} type="street" color="secondary" size="small" />
    </Box>
  );

  const hasInstallLocation = device.installLatitude != null && device.installLongitude != null;
  const hasInstall = device.installTime || hasInstallLocation;

  return (
    <DialogView
      position={position}
      open={open}
      handleClose={handleClose}
      dialogTitle={t('sharedInformation')}
      cancelTitle={t('sharedClose')}
      content={position && (
        <Table size="small">
          <TableBody>
            <SectionRow title={t('sharedDevice')} first />
            <StatusRow
              key="deviceIdentifier"
              name={t('deviceIdentifier')}
              content={<CopyableText text={`${device.uniqueId}`} />}
            />

            {hasInstall && (
              <SectionRow title={t('deviceInstallation')} />
            )}
            {device.installTime && (
              <StatusRow
                key="deviceInstallTime"
                name={t('deviceInstallTime')}
                content={formatTime(device.installTime, 'date', false)}
              />
            )}
            {hasInstallLocation && (
              <StatusRow
                key="deviceInstallAddress"
                name={t('positionAddress')}
                content={(
                  <AddressValue
                    latitude={device.installLatitude}
                    longitude={device.installLongitude}
                  />
                )}
              />
            )}
            {hasInstallLocation && (
              <StatusRow
                key="deviceInstallCoordinates"
                name={t('sharedCoordinates')}
                content={coordinatesContent(device.installLatitude, device.installLongitude)}
              />
            )}

            <SectionRow title={t('sharedCurrentPosition')} />
            <StatusRow
              key="fixTime"
              name={positionAttributes.fixTime.name}
              content={(
                <PositionValue
                  position={position}
                  property="fixTime"
                />
              )}
            />
            <StatusRow
              key="serverTime"
              name={positionAttributes.serverTime.name}
              content={(
                <PositionValue
                  position={position}
                  property="serverTime"
                />
              )}
            />
            <StatusRow
              key="address"
              name={t('positionAddress')}
              content={(
                <AddressValue
                  latitude={position.latitude}
                  longitude={position.longitude}
                  originalAddress={position.address}
                />
              )}
            />
            <StatusRow
              key="coordinates"
              name={t('sharedCoordinates')}
              content={coordinatesContent(position.latitude, position.longitude)}
            />

            <SectionRow title={t('sharedDistance')} />
            <StatusRow
              key="totalDistance"
              name={t('sharedTraveled')}
              content={formatDistance(position.attributes.totalDistance, distanceUnit, t)}
            />
            {device.originalDistance !== 0 && (
              <StatusRow
                key="vehicleTotalDistance"
                name={t('sharedTotal')}
                content={formatDistance(position.attributes.totalDistance + device.originalDistance, distanceUnit, t)}
              />
            )}
            {device.thirtyDayDistance !== 0 && (
              <StatusRow
                key="sharedLast30Days"
                name={t('sharedLast30Days')}
                content={(<div className={classes[get30DayDistanceColor(device.thirtyDayDistance, distanceUnit)]}>{formatDistance(device.thirtyDayDistance, distanceUnit, t)}</div>)}
              />
            )}
          </TableBody>
        </Table>
      )}
    />
  );
};

export default PropsDialog;
